/* Optimized Helper Utilities for Map Markers */

import moment from "moment";

export const GPS_MOVING_STALE_MINUTES = 5;

// Retrieve the company ID unless the user is a super_admin
const enumUserRole = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin'
};
export const getCompanyId = user =>
  user?.role?.value !== enumUserRole.SUPER_ADMIN ? user.companyId : null;

// Get the company's preferred marker format or default to 'registerNumber'
export const getCompanyFormat = user =>
  user?.company?.format || 'registerNumber';

export const getCompany = user => {
  let companyId = null;
  if (user && user.role && user.role.value !== enumUserRole.SUPER_ADMIN) {
    companyId = {
      ...user.company,
      id: user.company.id,
      label: user.company.name,
      value: user.company.id
    };
  }
  return companyId;
};


// Build a comma-separated group ID string for non-admin users
export const getGroupFilterQuery = (key, type, user) => {
  const role = getRoleName(user);
  if (role === enumUserRole.SUPER_ADMIN || role === enumUserRole.ADMIN) return null;
  return Array.isArray(user?.group) && user.group.length
    ? user.group.map(item => item.id).join(',')
    : null;
};

export const getUserGroupFilterQuery = (key, type, user) => {
  let query = "";
  let queryId = [];
  let userRole = getRoleName(user);
  if (userRole != "super_admin" && userRole != "admin") {
    if (user && user.group) {
      for (let item of user.group) {
        queryId.push(item.id);
      }
      if (0 < queryId.length) {
        query = "&filter_" + key + "=" + queryId.toString();
      }
    }
  }
  return query;
};


export const getUserParentGroupFilterQuery = (key, id, user) => {
  let query = "";
  let queryId = [];
  if (id) {
    if (user && user.parentGroup) {
      for (let item of user.parentGroup) {
        if (item.parentGroupId == id) {
          queryId.push(item.id);
        }
      }
    }
    if (0 < queryId.length) {
      query = "&filter_" + key + "=" + queryId.toString();
    } else {
      query = "&filter_" + key + "=-1"//no data
    }
  }
  return query;
};

// Extract the user's role value
export const getRoleName = user => user?.role?.value || '';

// Text width measurement using Canvas (avoids DOM manipulation)
const measureText = (() => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  return (text, font = "12px Arial") => {
    ctx.font = font;
    return ctx.measureText(text).width;
  };
})();

/**
 * Choose a stop or rotating arrow SVG based on movement.
 * @param {string} type
 * @param {number} course
 * @param {number} speed (knots)
 * @param {object} gpsInfo
 * @param {number} overSpeed (km/h threshold)
 * @returns {string} inline-SVG HTML
 */
export function getMapIcon(type, course, speed, fixtime, overSpeed) {

  // STOP SVG (parked)
  const stopSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="8" fill="#ffffff" stroke="#ff0000" stroke-width="2"/>
      <rect x="6" y="6" width="6" height="6" fill="#ff0000"/>
    </svg>
  `.trim();

  // ARROW SVG (moving)
  const arrowSvg = `
    <svg xmlns="http://www.w3.org/2000/svg"
         width="18" height="18" viewBox="0 0 18 18"
         style="transform:rotate(${course}deg);transform-origin:center;display:block">
      <circle cx="9" cy="9" r="8" fill="#007bff"/>
      <polygon points="9,5 5,11 7,11 7,15 11,15 11,11 13,11 9,5" fill="#ffffff"/>
    </svg>
  `.trim();

  return gpsItsOff(fixtime, speed) === 'moving' ? arrowSvg : stopSvg;
}

export function gpsItsOff(fixtime, speed) {
  let newGpsInfo = {};
  newGpsInfo.state = 'nogps';
  let diff = getDiffMiuntes(fixtime);
  if (43200 < diff) { //720 hours no active
    newGpsInfo.state = 'nogps';
  } else if (0 < speed) {
    newGpsInfo.state = 'moving';
    if (GPS_MOVING_STALE_MINUTES < diff) { // stale update
      newGpsInfo.state = 'stopped';
    }
  } else {
    newGpsInfo.state = 'stopped';
  }
  return newGpsInfo.state;
}

export function showContent(trackpoint, authUser, intl, options = {}) {
  const registerNumber = trackpoint.registerNumber;
  const hiddenFields = new Set(options.hideFields || []);
  const shouldShow = (field) => !hiddenFields.has(field);
  const lat = trackpoint.latitude;
  const lng = trackpoint.longitude;
  const googleUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const wazeUrl = `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  const streetUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
  const formatLabel = (label) => `${label}&nbsp;&nbsp;: `;
  const driverLine = shouldShow('driver')
    ? `${formatLabel(intl.formatMessage({ id: "gps.driver" }))}${trackpoint.driverName || "-"}`
    : null;
  const speedLine = shouldShow('speed')
    ? `${formatLabel(intl.formatMessage({ id: "gps.speed(Km/Hr)" }))}${getNumberFormat(trackpoint.speed || 0)}`
    : null;
  const orientationLine = shouldShow('orientation')
    ? `${formatLabel(intl.formatMessage({ id: "gps.orientation" }))}${intl.formatMessage({ id: `gps.${getDirection(trackpoint.course)}` })}`
    : null;
  const coordinatesLine = shouldShow('coordinates')
    ? `${formatLabel(intl.formatMessage({ id: "gps.coordinates" }))}${getNumberFormat(trackpoint.latitude)} ${getNumberFormat(trackpoint.longitude)}`
    : null;
  const formatTravelTime = (minutes) => {
    if (!Number.isFinite(minutes) || minutes < 0) {
      return '';
    }
    if (minutes < 1) {
      return intl.formatMessage({ id: 'gps.travelTimeLessMinute', defaultMessage: '<1 min' });
    }
    const totalMinutes = Math.round(minutes);
    if (totalMinutes < 60) {
      return intl.formatMessage({ id: 'gps.travelTimeMinutes', defaultMessage: '{value} min' }, { value: totalMinutes });
    }
    const hours = Math.floor(totalMinutes / 60);
    const remaining = totalMinutes % 60;
    if (remaining === 0) {
      return intl.formatMessage({ id: 'gps.travelTimeHours', defaultMessage: '{value} h' }, { value: hours });
    }
    return intl.formatMessage({ id: 'gps.travelTimeHoursMinutes', defaultMessage: '{hours} h {minutes} min' }, { hours, minutes: remaining });
  };

  const distanceLine = Number.isFinite(trackpoint.distanceToUserKm)
    ? `${intl.formatMessage({ id: 'gps.distanceToYou', defaultMessage: 'Distance to you' })} : ${trackpoint.distanceToUserKm.toFixed(2)} km`
    : null;
  const travelTimeLine = Number.isFinite(trackpoint.travelTimeMinutes)
    ? `${intl.formatMessage({ id: 'gps.travelTime', defaultMessage: 'Estimated travel time' })} : ${formatTravelTime(trackpoint.travelTimeMinutes)}`
    : null;

  const formatKey = getCompanyFormat(authUser);
  const vehicleIdLabel = intl.formatMessage({ id: `gps.format.${formatKey}` });
  const vehicleIdValue = trackpoint[formatKey] || "-";
  const dateTimeLine = `${intl.formatMessage({ id: "gps.date" })} : ${getFormatDate(trackpoint.fixtime, authUser)}  ${fullFormat24Time(trackpoint.fixtime, authUser)}`;

  const infoLines = [
    dateTimeLine,
    `${vehicleIdLabel} : ${vehicleIdValue}`,
  ];

  if (speedLine) infoLines.push(speedLine);
  if (orientationLine) infoLines.push(orientationLine);
  if (coordinatesLine) infoLines.push(coordinatesLine);
  if (driverLine) infoLines.push(driverLine);
  if (distanceLine) infoLines.push(distanceLine);
  if (travelTimeLine) infoLines.push(travelTimeLine);

  infoLines.push("");

  const detailsHtml = infoLines
    .filter((line) => line !== null && line !== undefined)
    .map((line) => (line === "" ? "" : line))
    .join("<br/>");

  const addressHtml = `<span style="white-space: pre-line;">${intl.formatMessage({ id: "gps.address" })} : ${trackpoint.address || "-"}</span>`;
  const combinedDetails = detailsHtml
    ? `${detailsHtml}${detailsHtml.endsWith("<br/>") ? "" : "<br/>"}${addressHtml}`
    : addressHtml;
  const popup = `
    <div style="width:300px; padding-top: 6px !important;">
      <p align="left" style="font-size: 12px; line-height: 18px; margin-top: 0px;">
        ${combinedDetails}
      </p>
      <div style="text-align:left; margin-top:6px; margin-bottom:5px; font-weight:bold; font-size:12px;">${intl.formatMessage({ id: "gps.navigation" })}</div>
      <div style="text-align:left; margin-top:4px;">
        <a
          id="googleButton-${registerNumber}"
          href="${googleUrl}"
          target="_blank"
          rel="noopener noreferrer"
          style="
            display:inline-flex;
            align-items:center;
            color:#fff;
            background-color:#3ca0ed;
            border:none;
            padding:6px 10px;
            font-size:12px;
            margin-top:-5px;
            font-weight:bold;
            margin-right:6px;
            border-radius:5px;
            text-decoration:none;
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" focusable="false" style="margin-right:6px;display:block">
            <path d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/>
          </svg>
          Google
        </a>
        <a
          id="wazeButton-${registerNumber}"
          href="${wazeUrl}"
          target="_blank"
          rel="noopener noreferrer"
          style="
            display:inline-flex;
            align-items:center;
            color:#fff;
            background-color:#3ca0ed;
            border:none;
            padding:6px 10px;
            font-size:12px;
            margin-top:-5px;
            font-weight:bold;
            margin-right:6px;
            border-radius:5px;
            text-decoration:none;
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" focusable="false" style="margin-right:6px;display:block">
            <path d="M5 13c0-3.866 3.134-7 7-7 3.53 0 6.467 2.61 6.94 6h.56a1.5 1.5 0 0 1 0 3h-1.2c-.552 1.165-1.738 2-3.1 2-.82 0-1.57-.297-2.14-.79-.456.32-.997.54-1.56.64l-.53.09-.25.45A2.5 2.5 0 1 1 8 15H7a2 2 0 1 1-2-2h0zm3 3a1.5 1.5 0 1 0 2.598 1.5L11 18.5c-.402-.07-.79-.215-1.134-.418A1.49 1.49 0 0 0 8 16h0zm9-2c.828 0 1.5-.672 1.5-1.5S17.828 10 17 10s-1.5.672-1.5 1.5S16.172 14 17 14z"/>
          </svg>
          Waze
        </a>
        <a
          id="streetViewButton-${registerNumber}"
          href="${streetUrl}"
          target="_blank"
          rel="noopener noreferrer"
          style="
            display:inline-flex;
            align-items:center;
            color:#fff;
            background-color:#3ca0ed;
            border:none;
            padding:6px 10px;
            font-size:12px;
            margin-top:-5px;
            font-weight:bold;
            margin-right:0;
            border-radius:5px;
            text-decoration:none;
          "
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" aria-hidden="true" focusable="false" style="margin-right:6px;display:block">
            <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4zm-6 15a6 6 0 1 1 12 0v3H6v-3zm6-4a4 4 0 0 0-4 4v1h8v-1a4 4 0 0 0-4-4z"/>
          </svg>
          ${intl.formatMessage({ id: "gps.streetview" })}
        </a>
      </div>
    </div>
  `;
  return popup;
}

export const getFormatDate = (date, authUser) => {
  const newCompany = getCompany(authUser);
  if (date && date !== "") {
    var e = new Date(date);
    return moment(e).format(newCompany == null ? "DD-MM-YYYY" : newCompany.dateformat);
  } else {
    return "";
  }
};

export const fullFormat24Time = (value, authUser) => {
  const newCompany = getCompany(authUser);
  let timeFormat = newCompany ? newCompany.timeformat : 'hh:mm:ss A';
  if (timeFormat === 'HH:mm') {
    timeFormat = newCompany.timeformat + ':ss';
  } else {
    timeFormat = 'hh:mm:ss A';
  }
  return moment(value).format(timeFormat);
};

export const getDirection = (angle) => {
  var directions = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  var index = Math.round(((angle %= 360) < 0 ? angle + 360 : angle) / 45) % 8;
  return directions[index];
}

/**
 * Builds the HTML + dimensions for a tooltip-style bubble marker.
 * @param {object} authUser
 * @param {number} course
 * @param {object} obj (must contain type, speed, gpsInfo, id, name, and display key)
 * @returns {{ html: string, width: number, height: number }}
 */
export function getHtmlMarker(authUser, course, obj) {
  const { type, speed = 0, id, name, fixtime } = obj;
  const formatKey = getCompanyFormat(authUser);
  const displayName = type === 'charging'
    ? id
    : obj[formatKey] || name;

  // Speed in km/h
  const kmh = (speed);
  const label = speed > 0
    ? `${displayName} a ${getNumberFormat(kmh)} Km/Hr`
    : displayName;

  // Inline SVG icon
  const overSpeed = authUser?.company?.overSpeed ?? Infinity;
  const iconSvg = getMapIcon(type, course, speed, fixtime, overSpeed);

  // Layout calculations
  const padding = 6;   // px each side
  const iconWidth = 18;  // SVG width
  const gap = 6;   // space between icon and text
  const textWidth = Math.ceil(measureText(label));
  const bubbleW = iconWidth + gap + textWidth + padding * 2;
  const bubbleH = 20;  // px height of the text line
  const arrowH = 6;   // px height of the downward triangle

  // Bubble + arrow HTML
  const html = `
    <div style="padding-bottom:6px !important;padding-top:4px !imporant;display:inline-flex;align-items:center;background:#fdfda3;padding:2px ${padding}px;border-radius:4px;font:bold 12px Arial;white-space:nowrap;">
      ${iconSvg}
      <span style="margin-left:${gap}px;">${label}</span>
    </div>
    <div style="width:0;height:0;border-left:${arrowH}px solid transparent;border-right:${arrowH}px solid transparent;border-top:${arrowH}px solid #fdfda3;margin:0 auto;"></div>
  `;

  return {
    html,
    width: bubbleW,
    height: bubbleH + arrowH,
  };
}

let cachedNumberFormatter = null;
let cachedAuthUserRaw = null;

const resolveNumberFormatter = () => {
  let locale = "en";
  if (typeof localStorage !== "undefined") {
    const authUserRaw = localStorage.getItem("authUser");
    if (authUserRaw !== cachedAuthUserRaw) {
      cachedAuthUserRaw = authUserRaw;
      if (authUserRaw) {
        try {
          const parsed = JSON.parse(authUserRaw);
          locale = parsed?.company?.language === "spanish" ? "es" : "en";
        } catch (_err) {
          locale = "en";
        }
      }
      cachedNumberFormatter = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
  }
  if (!cachedNumberFormatter) {
    cachedNumberFormatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }
  return cachedNumberFormatter;
};

export const getNumberFormat = (number, maximumFractionDigits = 2) => {
  if (
    number &&
    number !== Infinity &&
    number !== "Infinity" &&
    !isNaN(number)
  ) {
    number = parseFloat((number));
  } else {
    number = 0;
  }

  // Set maximumFractionDigits and minimumFractionDigits to 2
  maximumFractionDigits = 2;
  const formatter = resolveNumberFormatter();
  return formatter.format(number);
};
// Convert between units
export const convertToFormat = (value, unit) => {
  const val = Math.abs(Number(value) || 0);
  const conversions = {
    mi: val * 1000,
    km: val / 1000,
    knot: val / 1.852,
    kmh: val * 1.852,
    ms: val * 3600000,
    hours: val / 3600000,
    litre: val,
  };
  return conversions[unit]?.toFixed(2) || '0.00';
};

export const getDiffMiuntes = (date1) => {
  var ago = moment(new Date(date1));
  let now = moment(new Date());
  let diff = now.diff(ago, 'minutes')
  return diff;
}

export const getFilterQuery = (key, list) => {
  if (Array.isArray(list)) {
    const values = list
      .map(item => item.value)
      .filter(v => v !== undefined && v !== null && v !== "");
    if (!values.length) return "";
    return `&filter_${key}=${values.join(',')}`;
  }

  const val = list?.value ?? list;
  if (val === undefined || val === -1 || val === "") {
    return "";
  }
  return `&filter_${key}=${val}`;
};
